/**
 * Общие константы и нормализация модели курса витрины корпоративного обучения.
 *
 * Модель описана в src/pages/Courses/courses.types.js: текстовые поля — триплеты
 * {ru, kk, en}. Админка редактирует ровно эту форму объекта, поэтому между формой
 * и MarketCourseService никакой конвертации нет — только дозаполнение пропусков.
 */

/** Языки заполнения. Порядок задаёт порядок кнопок переключателя над формой. */
export const LANGS = [
    {code: 'ru', labelKey: 'marketCoursesPage.lang.ru'},
    {code: 'kk', labelKey: 'marketCoursesPage.lang.kk'},
    {code: 'en', labelKey: 'marketCoursesPage.lang.en'},
];

/** Коды языков отдельным списком — по нему ищем язык в путях ошибок валидации. */
export const LANG_CODES = LANGS.map((lang) => lang.code);

/** Слаг участвует в публичном /courses/:slug, поэтому только латиница-кебаб. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Триплет из значений формы. Язык, который редактор не тронул, приходит undefined —
 * отдаём его пустой строкой, а не пропускаем: на витрине чтение идёт по фиксированным
 * ключам и отсутствующий ключ пришлось бы обрабатывать отдельно.
 */
export const toText = (value) => ({
    ru: (value?.ru ?? '').trim(),
    kk: (value?.kk ?? '').trim(),
    en: (value?.en ?? '').trim(),
});

/** Текст для админской таблицы: русский, а если его нет — любой заполненный язык. */
export const previewText = (value) => value?.ru || value?.kk || value?.en || '';

/** Пустое числовое поле — это null, а не 0: 0 тенге и «цена не указана» разные вещи. */
const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
};

/** Значения нового курса. Списки заводим пустыми, чтобы Form.List было что рендерить. */
export const emptyCourseValues = () => ({
    slug: '',
    sortOrder: 1,
    visible: true,
    audienceTags: [],
    trainer: {isPlaceholder: false, photo: null},
    modules: [],
    outcomes: [],
    targetAudience: [],
    forBusiness: [],
    faq: [],
});

/**
 * Ответ бэкенда -> значения формы. Триплеты уже в нужном виде, дозаполняем только
 * коллекции и trainer: Form.List на undefined падает, а Switch без значения молча
 * остаётся выключенным.
 */
export const toFormValues = (course) => ({
    ...course,
    audienceTags: course?.audienceTags ?? [],
    modules: course?.modules ?? [],
    outcomes: course?.outcomes ?? [],
    targetAudience: course?.targetAudience ?? [],
    forBusiness: course?.forBusiness ?? [],
    faq: course?.faq ?? [],
    trainer: {isPlaceholder: false, photo: null, ...(course?.trainer ?? {})},
});

/**
 * Значения формы -> тело запроса. Собираем поле за полем, а не спредом values:
 * так в запрос гарантированно уходит вся модель целиком и ничего лишнего.
 *
 * Преподаватель отдельной сущностью не хранится: его поля лежат колонками в самом
 * курсе, поэтому никакого trainer.id в контракте нет и слать его не надо.
 *
 * @param values результат form.validateFields()
 * @param id     id редактируемого курса; null означает создание
 */
export const buildPayload = (values, {id = null} = {}) => ({
    id: id ?? null,
    slug: (values.slug ?? '').trim(),
    sortOrder: toNumber(values.sortOrder) ?? 0,
    visible: !!values.visible,
    title: toText(values.title),
    subtitle: toText(values.subtitle),
    cardDescription: toText(values.cardDescription),
    category: values.category ?? null,
    format: values.format ?? null,
    level: values.level ?? null,
    audienceTags: values.audienceTags ?? [],
    durationHours: toNumber(values.durationHours) ?? 0,
    durationShape: toText(values.durationShape),
    priceTenge: toNumber(values.priceTenge),
    minGroupSize: toNumber(values.minGroupSize),
    corporateFromSize: toNumber(values.corporateFromSize),
    goal: toText(values.goal),
    finalProduct: toText(values.finalProduct),
    targetAudience: (values.targetAudience ?? []).map(toText),
    forBusiness: (values.forBusiness ?? []).map(toText),
    outcomes: (values.outcomes ?? []).map((outcome) => ({
        title: toText(outcome?.title),
        text: toText(outcome?.text),
    })),
    // n — номер модуля на витрине, держим его равным порядку строк в форме
    modules: (values.modules ?? []).map((module, index) => ({
        n: toNumber(module?.n) ?? index + 1,
        title: toText(module?.title),
        content: toText(module?.content),
        hours: toNumber(module?.hours) ?? 0,
    })),
    trainer: {
        isPlaceholder: !!values.trainer?.isPlaceholder,
        name: toText(values.trainer?.name),
        position: toText(values.trainer?.position),
        credentials: toText(values.trainer?.credentials),
        why: toText(values.trainer?.why),
        photo: values.trainer?.photo || null,
    },
    faq: (values.faq ?? []).map((item) => ({
        q: toText(item?.q),
        a: toText(item?.a),
    })),
});
